import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TechRadar = ({ category, technologies, isActive, onTechSelect }) => {
  const [rotation, setRotation] = useState(0);
  const [hoveredTech, setHoveredTech] = useState(null);

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(() => {
        setRotation(prev => (prev + 0.5) % 360);
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isActive]);

  const getPositionOnCircle = (index, total, radius = 120) => {
    const angle = (index * 360) / total;
    const radian = (angle * Math.PI) / 180;
    return {
      x: Math.cos(radian) * radius,
      y: Math.sin(radian) * radius,
      angle
    };
  };

  return (
    <div className="relative w-80 h-80 mx-auto">
      {/* Radar Grid */}
      <div className="absolute inset-0 rounded-full border-2 border-brand-cyan/30 cyber-glow">
        <div className="absolute inset-4 rounded-full border border-brand-cyan/20"></div>
        <div className="absolute inset-8 rounded-full border border-brand-cyan/10"></div>
        <div className="absolute inset-12 rounded-full border border-brand-cyan/5"></div>
      </div>

      {/* Radar Sweep */}
      {isActive && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(from ${rotation}deg, transparent 0deg, rgba(0, 212, 255, 0.3) 30deg, transparent 60deg)`
          }}
          animate={{ rotate: rotation }}
          transition={{ duration: 0.1, ease: "linear" }}
        />
      )}

      {/* Center Hub */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-full cyber-glow-intense flex items-center justify-center">
        <Icon name="Cpu" size={24} color="#ffffff" />
      </div>

      {/* Technology Orbs */}
      {technologies.map((tech, index) => {
        const position = getPositionOnCircle(index, technologies.length);
        const proficiencyRadius = 8 + (tech.proficiency / 100) * 12;
        
        return (
          <motion.div
            key={tech.id}
            className="absolute cursor-pointer group"
            style={{
              left: `calc(50% + ${position.x}px - ${proficiencyRadius}px)`,
              top: `calc(50% + ${position.y}px - ${proficiencyRadius}px)`
            }}
            whileHover={{ scale: 1.2 }}
            onHoverStart={() => setHoveredTech(tech)}
            onHoverEnd={() => setHoveredTech(null)}
            onClick={() => onTechSelect(tech)}
          >
            <div
              className={`w-${Math.floor(proficiencyRadius/2)} h-${Math.floor(proficiencyRadius/2)} rounded-full cyber-glow transition-all duration-300 flex items-center justify-center`}
              style={{
                width: `${proficiencyRadius * 2}px`,
                height: `${proficiencyRadius * 2}px`,
                background: `linear-gradient(135deg, ${tech.color}80, ${tech.color}40)`
              }}
            >
              <Icon name={tech.icon} size={proficiencyRadius} color="#ffffff" />
            </div>
            
            {/* Tech Label */}
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-surface/90 backdrop-blur-sm px-2 py-1 rounded text-xs text-foreground whitespace-nowrap cyber-border">
                {tech.name}
              </div>
            </div>
          </motion.div>
        );
      })}

      {/* Category Label */}
      <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 text-center">
        <h3 className="font-orbitron font-bold text-lg text-brand-cyan">{category}</h3>
        <p className="text-sm text-text-secondary mt-1">{technologies.length} Technologies</p>
      </div>

      {/* Hover Details */}
      {hoveredTech && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-64"
        >
          <div className="glass-morphism p-4 rounded-lg cyber-border">
            <div className="flex items-center space-x-3 mb-2">
              <Icon name={hoveredTech.icon} size={20} color={hoveredTech.color} />
              <h4 className="font-exo font-semibold text-foreground">{hoveredTech.name}</h4>
            </div>
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-xs text-text-secondary">Proficiency:</span>
              <div className="flex-1 bg-surface rounded-full h-2">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${hoveredTech.proficiency}%`,
                    background: `linear-gradient(90deg, ${hoveredTech.color}, ${hoveredTech.color}80)`
                  }}
                />
              </div>
              <span className="text-xs font-mono text-brand-cyan">{hoveredTech.proficiency}%</span>
            </div>
            <p className="text-xs text-text-secondary">{hoveredTech.description}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TechRadar;